import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { rtl } from '../../../utils/language';

const Wrapper = styled.div`
  ${({ fullWidth }) =>
    fullWidth &&
    `
    width: 100%;
  `};

  .select,
  .select__content {
    font-weight: 400;
    border-radius: 5px;
    border: 2px solid #ededed;
  }
  .select {
    background-color: #fff;
    cursor: pointer;
    display: block;
    font-size: 16px;
    transition: 0.3s;
  }
  .select.is-open {
    border-color: #16acb7;
  }
  .select.is-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  .select__button {
    align-items: center;
    border-radius: 5px;
    color: #485465;
    display: flex;
    justify-content: space-between;
    min-height: 34px;
    padding: 6px 12px;
    position: relative;
  }
  .select__button.with-pulse {
    animation: shadow-pulse 1.3s infinite;
  }
  .is-open .select__button {
    animation: none;
  }
  .ar .select__button {
    flex-direction: row-reverse;
  }
  .select__button-arrow {
    font-size: 12px;
    transition: 0.3s;
  }
  .ar .select__button-arrow {
    margin-left: unset;
    margin-right: var(--half-spacing-unit);
  }
  .is-open .select__button-arrow {
    transform: rotate(180deg);
  }
  .is-disabled .select__button-arrow {
    color: var(--mercury);
  }
  .select__text,
  .select__placeholder {
    ${rtl`
      text-align: right;
      direction: rtl;
  `};
  }
  .select__placeholder {
    color: var(--dusty-gray);
  }
  .select__content {
    animation: fadeIn 0.3s;
    background-color: #fff;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
    color: rgba(0, 1, 0);
    display: none;
    left: 0;
    max-height: 200px;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    position: absolute;
    top: calc(100% + var(--half-spacing-unit) / 2);
    width: 100%;
    z-index: 998;
  }
  .is-open .select__content {
    border-color: var(--eastern-blue);
    display: block !important;
    text-align: left;
  }
  .select__item {
    align-items: center;
    cursor: pointer;
    display: flex;
    padding: 6px 12px;
    text-decoration: none;
    transition: 0.3s;
    overflow-x: hidden;
  }
  .ar .select__item {
    direction: rtl;
  }
  .select__item.is-selected {
    background-color: #ececec;
  }
  .select__item.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .select__item:hover {
    background-color: var(--mercury);
  }
`;

class Select extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
    this.parentNode = null;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = event => {
    if (this.parentNode && !this.parentNode.contains(event.target))
      this.setState({ isOpen: false });
  };

  setParentNode = node => {
    this.parentNode = node;
  };

  toggleMenu = () => {
    if (this.props.disabled) return;
    this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
  };

  getItemTranslation = item => {
    const { textKey, lang, t } = this.props;
    const i18nTranslation = t(item[textKey]);
    if (i18nTranslation) {
      return i18nTranslation;
    }
    return item.locale && item.locale[lang] ? item.locale[lang] : item[textKey];
  };

  generateMenuItems = () => {
    const { menuItems, selectedItem, textKey } = this.props;

    return menuItems.map(item => {
      const isSelected =
        selectedItem && item[textKey] === selectedItem[textKey];

      return (
        <div
          onClick={() => {
            if (!isSelected) {
              this.props.selectionChanged(item);
            }
          }}
          className={`select__item ${isSelected ? 'is-selected' : ''}`}
          key={item[textKey]}
        >
          <span data-testid="level-value">{this.getItemTranslation(item)}</span>
        </div>
      );
    });
  };

  render() {
    const { isOpen } = this.state;
    const {
      menuItems,
      selectedItem,
      className,
      placeholder,
      testId,
      fullWidth,
      disabled,
    } = this.props;
    return (
      <Wrapper className="relative" fullWidth={fullWidth}>
        <div
          className={`select ${className} ${isOpen ? 'is-open' : ''} ${
            !menuItems.length ? 'is-disabled' : ''
          }`}
          data-qa="select-component"
          data-disabled={disabled}
          onClick={this.toggleMenu}
          ref={this.setParentNode}
          data-testid={testId}
        >
          <div
            className={`select__button ${
              !selectedItem && menuItems.length ? 'with-pulse' : ''
            }`}
            data-testid="select-selected-text"
          >
            {selectedItem ? (
              <span
                className="text-truncate select__text"
                data-testid={`${testId}-selected-item`}
              >
                {this.getItemTranslation(selectedItem)}
              </span>
            ) : (
              <span className="select__placeholder text-truncate">
                {placeholder}
              </span>
            )}
            <i className="assignments-icon icon--arrow select__button-arrow" />
          </div>
          {!!menuItems.length && (
            <div
              className="select__content"
              data-testid="select-content-wrapper"
            >
              {this.generateMenuItems()}
            </div>
          )}
        </div>
      </Wrapper>
    );
  }
}

Select.defaultProps = {
  className: '',
  menuItems: [],
  placeholder: '',
  selectedItem: null,
  testId: null,
  lang: 'en',
  fullWidth: false,
  disabled: false,
};

Select.propTypes = {
  textKey: PropTypes.string.isRequired,
  selectionChanged: PropTypes.func.isRequired,
  className: PropTypes.string,
  menuItems: PropTypes.array,
  placeholder: PropTypes.string,
  selectedItem: PropTypes.object,
  testId: PropTypes.string,
  lang: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Select);
